/* eurus-atc-tracking.js — standalone passive tracking module.
 *
 * Emits Add-to-cart and Buy-with-Shop intent events via Shopify.analytics.publish,
 * where the ga4-tracking Custom Pixel picks them up.
 *
 * Design contract:
 *   - Zero modification to existing theme files (no patches to product-cart.js etc).
 *   - Pure passive listeners at document level (event delegation).
 *   - Every callback wrapped in try/catch — never affects store behaviour.
 *   - Kill switches: window.EURUS_TRACKING_DISABLED, ?eurus_tracking=off, Shopify.designMode.
 *   - Loaded with `defer` and initialised on idle — never blocks rendering.
 */
(function () {
  'use strict';

  // ---------- kill switches ----------
  try {
    if (typeof window === 'undefined') return;
    if (window.__EURUS_ATC_TRACKING_INITIALISED__) return;
    window.__EURUS_ATC_TRACKING_INITIALISED__ = true;
    if (window.EURUS_TRACKING_DISABLED === true) return;
    if (window.Shopify && window.Shopify.designMode) return;
    var qs = (window.location && window.location.search) || '';
    if (qs.indexOf('eurus_tracking=off') !== -1) return;
  } catch (e) { return; }

  var DEBUG = false;
  try {
    DEBUG = localStorage.getItem('debugEurusAtcTracking') === 'true'
         || (window.location && window.location.search.indexOf('eurus_tracking_debug=1') !== -1);
    if (window.location.search.indexOf('eurus_tracking_debug=1') !== -1) {
      try { localStorage.setItem('debugEurusAtcTracking', 'true'); } catch (e) {}
    }
  } catch (e) { /* noop */ }

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[eurus-atc]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  // ---------- safe wrapper ----------
  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (err) {
        try { console.error('[eurus-atc] handler failed', err); } catch (e) {}
      }
    };
  }

  // ---------- utilities ----------
  function uuid() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) {}
    return 'e-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // Template detection: first try the `template-foo` class (Dawn-style themes).
  // Eurus is Alpine-based and does NOT set a template- body class, so fall back
  // to URL path matching (`/products/...`, `/collections/...`, etc.).
  function templateFromBody() {
    try {
      var cls = (document.body && document.body.className) || '';
      var m = cls.match(/(?:^|\s)template-([a-z0-9-]+)/);
      if (m) return m[1];
      var path = ((window.location && window.location.pathname) || '').toLowerCase();
      if (path === '/' || path === '') return 'index';
      if (path.indexOf('/products/') === 0) return 'product';
      if (path.indexOf('/collections/') === 0) {
        return path.indexOf('/products/') !== -1 ? 'product' : 'collection';
      }
      if (path.indexOf('/pages/') === 0) return 'page';
      if (path.indexOf('/blogs/') === 0) return 'article';
      if (path.indexOf('/cart') === 0) return 'cart';
      if (path.indexOf('/search') === 0) return 'search';
      return '';
    } catch (e) { return ''; }
  }

  // CRITICAL: Shopify product forms contain `<input name="id" …>` (the variant
  // id input). Per HTML spec, a form control named `id` overrides `form.id` as
  // a DOM PROPERTY — `form.id` returns the input element, not a string. ALWAYS
  // read the form's id via getAttribute('id') instead.
  function getFormIdAttr(form) {
    if (!form || typeof form.getAttribute !== 'function') return '';
    var v = form.getAttribute('id');
    return typeof v === 'string' ? v : '';
  }

  function sectionElFromForm(form) {
    try {
      if (!form || !form.closest) return null;
      return form.closest('section[id^="shopify-section-"]');
    } catch (e) { return null; }
  }

  function sectionIdFromForm(form) {
    var sec = sectionElFromForm(form);
    return sec ? sec.id : '';
  }

  // Heuristic source classifier — DOM-derived, zero markup changes required.
  // Returns one of:
  //   pdp-main | pdp-sticky | quick-view | quick-add-card | bundle |
  //   frequently-bought | featured-product | video-with-text | choose-option |
  //   cart | unknown
  function classifySource(form, btn) {
    try {
      // Sticky bar — in eurus, the sticky button id contains "sticky"
      // (`x-atc-button-sticky-…`) and shares the same product form as the main
      // button, so we check BOTH the button id and the form id.
      var btnId = (btn && btn.id) ? btn.id.toLowerCase() : '';
      if (btnId.indexOf('sticky') !== -1) return 'pdp-sticky';
      var formId = getFormIdAttr(form).toLowerCase();
      if (formId.indexOf('_sticky') !== -1 || formId.indexOf('-sticky') !== -1) return 'pdp-sticky';

      // Quick-view modal — content is AJAX-injected into `#quickview-product-content`
      // (class `.quick-view`) which lives OUTSIDE any `<section id="shopify-section-…">`
      // wrapper. Section-id heuristics miss it, so detect the container directly.
      // This catches Shop Pay clicks in the modal opened from a homepage card,
      // collection card, or any surface that re-uses the quickview popup.
      if (form && form.closest('#quickview-product-content, .quick-view')) return 'quick-view';
      if (btn && btn.closest && btn.closest('#quickview-product-content, .quick-view')) return 'quick-view';

      // Section-id-driven classification — works for any non-PDP-main entry point.
      var sec = form ? sectionElFromForm(form) : null;
      var secId = sec ? (sec.id || '').toLowerCase() : '';
      if (secId.indexOf('quick-view') !== -1) return 'quick-view';
      if (secId.indexOf('featured-product') !== -1) return 'featured-product';
      if (secId.indexOf('video-with-text') !== -1) return 'video-with-text';
      if (secId.indexOf('choose-option') !== -1) return 'choose-option';

      if (form) {
        if (form.closest('[data-card-product-bundle], .card-product-bundle')) return 'bundle';
        if (form.closest('.card-product-frequently, [data-card-product-frequently]')) return 'frequently-bought';
        if (form.closest('.card-product, .card, .grid__item')) return 'quick-add-card';
      }

      // URL-path-driven fallbacks. `/cart` covers the Shop Pay button on the
      // cart page (rendered via {{ content_for_additional_checkout_buttons }}).
      // `/products/` covers PDP main when section detection didn't catch it.
      var path = ((window.location && window.location.pathname) || '').toLowerCase();
      if (path === '/cart' || path.indexOf('/cart?') === 0 || path === '/cart/' ||
          (path.indexOf('/cart') === 0 && path.indexOf('/cart/add') !== 0)) return 'cart';
      if (path.indexOf('/products/') !== -1) return 'pdp-main';

      var tpl = templateFromBody();
      if (tpl === 'product') return 'pdp-main';
      if (tpl === 'collection') return 'quick-add-card';
      if (tpl === 'cart') return 'cart';
      return 'unknown';
    } catch (e) { return 'unknown'; }
  }

  function pickVariantInput(form) {
    try {
      if (!form) return null;
      var hidden = form.querySelector('input[name="id"]');
      return hidden ? hidden.value : null;
    } catch (e) { return null; }
  }

  function pickQuantity(form) {
    try {
      if (!form) return 1;
      // A volume tier picker posts its checked radio; the first quantity input
      // would be its base row, or the stepper it hides.
      var q = form.querySelector('input[name="quantity"]:checked') ||
        form.querySelector('input[name="quantity"]:not([type="radio"])');
      var n = q ? parseInt(q.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    } catch (e) { return 1; }
  }

  function pickProductId(form) {
    try {
      if (!form) return '';
      var hidden = form.querySelector('input[name="product-id"], input[name="product_id"]');
      if (hidden && hidden.value) return hidden.value;
      var sec = sectionElFromForm(form);
      if (sec) {
        var pid = sec.getAttribute('data-product-id');
        if (pid) return pid;
      }
      return '';
    } catch (e) { return ''; }
  }

  // ---------- publish ----------
  function publish(name, params) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, params);
        log('publish', name, params);
      } else {
        log('publish skipped (Shopify.analytics unavailable)', name);
      }
    } catch (e) {
      try { console.error('[eurus-atc] publish failed', e); } catch (err) {}
    }
  }

  // ---------- handlers ----------
  function buildAtcParams(form, btn, eventId) {
    return {
      event_id: eventId,
      click_ts: Date.now(),
      source: classifySource(form, btn),
      section_id: sectionIdFromForm(form),
      template: templateFromBody(),
      product_id: pickProductId(form),
      variant_id: pickVariantInput(form) || '',
      quantity: pickQuantity(form),
      page_location: location.href,
      page_referrer: document.referrer || ''
    };
  }

  // Looks for `addToCart(` (or `handleAddToCart(` in video-with-text) in any
  // Alpine `@click[.modifier]="…"` attribute. This is the universal signal
  // across all theme entry points — including card quick-add and bundles,
  // which don't carry the `add_to_cart_button` class.
  function hasAddToCartHandler(btn) {
    if (!btn || !btn.attributes) return false;
    for (var i = 0; i < btn.attributes.length; i++) {
      var attr = btn.attributes[i];
      if (attr && typeof attr.name === 'string' &&
          attr.name.indexOf('@click') === 0 &&
          /addToCart\(/.test(attr.value || '')) {
        return true;
      }
    }
    return false;
  }

  // True if `btn` is an add-to-cart trigger. Multiple signals so we catch
  // every entry point without modifying any existing theme markup:
  //   1. `data-type="add-to-cart"` attribute
  //   2. classes: add_to_cart_button | x-atb-button | add-to-cart
  //   3. an Alpine `@click*="…addToCart(…)…"` handler
  // Quantity ±, variant pickers, share, vendor-open, etc. match none of these.
  function isAtcButton(btn) {
    if (!btn) return false;
    var dataType = (btn.getAttribute && btn.getAttribute('data-type')) || '';
    if (dataType === 'add-to-cart') return true;
    var cls = (btn.className || '').toString();
    if (/\bx-atb-button\b|\badd_to_cart_button\b|\badd-to-cart\b/.test(cls)) return true;
    return hasAddToCartHandler(btn);
  }

  function isAtcForm(form) {
    if (!form) return false;
    var action = (form.getAttribute('action') || '').toLowerCase();
    if (action.indexOf('/cart/add') !== -1) return true;
    var ft = (form.getAttribute('data-type') || '').toLowerCase();
    return ft === 'add-to-cart-form';
  }

  // Shop Pay / "Buy with Shop" is rendered as the <shop-pay-wallet-button>
  // (or <shop-pay-button>) custom element on this storefront — NOT an iframe.
  // Clicks on it (or any descendant) are a direct, high-confidence intent
  // signal — no heuristic needed.
  var SHOP_PAY_SELECTOR = 'shop-pay-wallet-button, shop-pay-button, shopify-payment-terms';

  // Primary path: click capture. The theme's Alpine `@click.prevent="addToCart(...)"`
  // calls e.preventDefault(), so the form never submits — meaning a 'submit'
  // listener alone misses PDP / sticky / quick-view / featured-product /
  // video-with-text / choose-option. Click capture fires regardless.
  var onClickCapture = safe(function (ev) {
    var el = ev.target;
    if (!el || !el.closest) return;

    // Shop Pay intent: direct click on the wallet button.
    var spHost = el.closest && el.closest('shop-pay-wallet-button, shop-pay-button');
    if (spHost) {
      var spForm = spHost.closest('form');
      if (!spForm) {
        var container = spHost.closest('[x-ref="product_form_container"]');
        spForm = container ? container.closest('form') : null;
      }
      var spId = uuid();
      // Pass spHost as the `btn` arg so classifySource can detect quickview-modal
      // context by walking the wallet button's ancestors (the modal isn't inside
      // a shopify-section wrapper, so the form-based section check misses it).
      var spParams = Object.assign({}, buildAtcParams(spForm, spHost, spId), { confidence: 'direct' });
      publish('eurus_shop_pay_intent', spParams);
      return;
    }

    var btn = el.closest('button, [role="button"], a');
    if (!isAtcButton(btn)) return;

    // The sticky button is linked to its form via the `form` attribute and is
    // OUTSIDE the form in DOM, so closest('form') would miss it. Fall back to
    // document.getElementById(btn.form) → the linked form element.
    var form = btn.closest('form');
    if (!form && btn.getAttribute && btn.getAttribute('form')) {
      form = document.getElementById(btn.getAttribute('form'));
    }
    if (form) form.__eurusTrackingLastClickTs = Date.now();

    var eventId = uuid();
    var params = buildAtcParams(form, btn, eventId);
    publish('eurus_atc_clicked', params);
  });

  // Backup path: native form submission (e.g., Enter key in qty input with
  // no Alpine intercept). De-duped against the click capture above so a
  // click + native-submit sequence only fires once.
  var onSubmitCapture = safe(function (ev) {
    var t = ev.target;
    if (!t || t.tagName !== 'FORM') return;
    if (!isAtcForm(t)) return;
    if (t.__eurusTrackingLastClickTs && (Date.now() - t.__eurusTrackingLastClickTs) < 500) return;

    var eventId = uuid();
    var params = buildAtcParams(t, null, eventId);
    publish('eurus_atc_clicked', params);
  });

  // ---------- Shop Pay (Buy with Shop) iframe-backed fallback ----------
  // Some themes / surfaces render `{{ form | payment_button }}` as a true
  // cross-origin iframe (e.g., gift card products, older theme variants).
  // For those cases, the click event can't be observed directly, so we
  // fall back to a hover + window-blur + iframe-focus heuristic. On this
  // storefront the primary signal is the direct click on <shop-pay-wallet-button>
  // above; this listener only fires if that path didn't match.
  var lastShopPayHover = 0;
  var shopPayContainerSelector = '[x-ref="product_form_container"], ' + SHOP_PAY_SELECTOR + ', .shopify-payment-button';

  var onShopPayHover = safe(function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    if (!t.closest(shopPayContainerSelector)) return;
    lastShopPayHover = Date.now();
  });

  var onWindowBlur = safe(function () {
    if (Date.now() - lastShopPayHover > 1500) return;
    var ae = document.activeElement;
    if (!ae || ae.tagName !== 'IFRAME') return;
    var container = ae.closest && ae.closest(shopPayContainerSelector);
    if (!container) return;

    var form = container.closest && container.closest('form');
    var eventId = uuid();
    var params = Object.assign({}, buildAtcParams(form, null, eventId), { confidence: 'heuristic' });
    publish('eurus_shop_pay_intent', params);
  });

  // ---------- init ----------
  // Note: success pairing (eurus_atc_succeeded) is intentionally NOT done here.
  // `eurus:cart:items-changed` fires on every cart mutation (remove, qty change,
  // re-order, drawer refresh), which would cause large over-counts. Pairing
  // happens in the Custom Pixel against Shopify's server-confirmed
  // `product_added_to_cart` event instead.
  function init() {
    try {
      document.addEventListener('click', onClickCapture, true);
      document.addEventListener('submit', onSubmitCapture, true);
      document.addEventListener('pointerenter', onShopPayHover, true);
      window.addEventListener('blur', onWindowBlur);
      log('initialised; template=', templateFromBody());
    } catch (e) {
      try { console.error('[eurus-atc] init failed', e); } catch (err) {}
    }
  }

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(init, { timeout: 2000 });
  } else {
    setTimeout(init, 0);
  }
})();
